import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import TimeUnits from './TimeUnits';

const defaultUnits: TimeUnits = {
    day: 'd',
    hour: 'h',
    minute: 'm',
    second: 's',
};

type Props = {
    nextDraw: string;
    nextNextDraw?: string;
    timeUnits?: TimeUnits;
    isThankYouPage?: boolean;
};

export default function UniversalDrawCounter({
    nextDraw,
    nextNextDraw = null,
    timeUnits = defaultUnits,
    isThankYouPage = null,
}: Props) {
    const drawTime = useRef(moment(nextDraw)).current;
    const secondsToNextDraw = useRef(moment(nextNextDraw).diff(drawTime, 'seconds')).current;
    const timeout = useRef<NodeJS.Timeout>(null);
    const [counterUnits, setCounterUnits] = useState([<>--</>, <>--</>]);

    function updateCounter() {
        const difference = Math.max(0, drawTime.unix() - moment().unix());

        timeout.current = setTimeout(updateCounter, 1000 - new Date().getMilliseconds());
        setCounterUnits(getRemainingTimeUnits(difference, timeUnits));

        if (difference === 0) {
            if (secondsToNextDraw > 0) {
                drawTime.add(secondsToNextDraw, 'seconds');
            } else {
                clearTimeout(timeout.current);
                if (isThankYouPage) {
                    const checkResultsButton = document.getElementById('check-results');
                    if (checkResultsButton) {
                        checkResultsButton.click();
                    }
                }
            }
        }
    }

    useEffect(() => {
        if (drawTime.isAfter(moment())) {
            updateCounter();

            return () => clearTimeout(timeout.current);
        }

        return null;
    }, []);

    return (
        <span className="countdown-clock-wrapper">
            <span className="counter-bg">{counterUnits[0]}</span>
            <span className="counter-bg"> : </span>
            <span className="counter-bg">{counterUnits[1]}</span>
        </span>
    );
}

function getRemainingTimeUnits(seconds: number, units: TimeUnits): [React.JSX.Element, React.JSX.Element] {
    const timeParts = {
        day: Math.floor(seconds / 86400),
        hour: Math.floor((seconds % 86400) / 3600),
        minute: Math.floor((seconds % 3600) / 60),
        second: Math.floor(seconds % 60),
    };

    const result = [];
    Object.keys(timeParts).forEach((key, index) => {
        if (result.length === 2) {
            return;
        }
        if (timeParts[key] === 0 && index < 2) {
            return;
        }

        result.push(
            <>
                {timeParts[key].toString().padStart(2, '0')}
                <span className="counter-units">{units[key]}</span>
            </>
        );
    });

    return result as [React.JSX.Element, React.JSX.Element];
}
